"use client";

import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";

export default function useLocalStorage<T>(key: string, initialValue?: T): [T | undefined, Dispatch<SetStateAction<T | undefined>>] {
    const [value, setValue] = useState<T>();
    const ref = useRef<T>(undefined);

    const setValue_event: Dispatch<SetStateAction<T | undefined>> = (v: SetStateAction<T | undefined>) => {
        setValue(v);

        const p = JSON.stringify({ v });
        localStorage.setItem(key, p);
        window.dispatchEvent(new Event("setStorage"));
    }

    useEffect(() => {
        function LoadParseStorage() {
            const historyVal = localStorage.getItem(key);
            try {
                //attempt parse
                let { v } = JSON.parse(historyVal || "") as { v: T };

                //if value might be a Date - attempt parse it
                if (typeof v === "string" && v.includes("-") && v.includes(":")) {
                    const asDate = new Date(v);
                    //check if valid date
                    if (!isNaN(Number(asDate)))
                        (v as any) = asDate;
                }
                if (v != value) {
                    setValue(v); //update state to value from storage
                    ref.current = v; //update ref
                }
            } catch (e) {
                //fallback to reset storage value
                localStorage.setItem(key, JSON.stringify({ v: initialValue }));
                window.dispatchEvent(new Event("setStorage"));
                setValue(initialValue); //initialize state
                ref.current = initialValue; //initialize ref
            }
        }

        //parse value on initial load
        LoadParseStorage();
        //update when ever storage gets changed
        window.addEventListener("setStorage", LoadParseStorage);
        return () => {
            //remove listener
            window.removeEventListener("setStorage", LoadParseStorage)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return [value, setValue_event];
}

export function useSessionStorage<T>(key: string, initialValue?: T): [T | undefined, Dispatch<SetStateAction<T | undefined>>] {
    const [value, setValue] = useState<T>();
    const ref = useRef<T>(undefined);

    const setValue_event: Dispatch<SetStateAction<T | undefined>> = (v: SetStateAction<T | undefined>) => {
        setValue(v);

        const p = JSON.stringify({ v });
        sessionStorage.setItem(key, p);
        window.dispatchEvent(new Event("setStorage"));
    }

    useEffect(() => {
        function LoadParseStorage() {
            const historyVal = sessionStorage.getItem(key);
            try {
                //attempt parse
                let { v } = JSON.parse(historyVal || "") as { v: T };

                //if value might be a Date - attempt parse it
                if (typeof v === "string" && v.includes("-") && v.includes(":")) {
                    const asDate = new Date(v);
                    //check if valid date
                    if (!isNaN(Number(asDate)))
                        (v as any) = asDate;
                }
                if (v != value) {
                    setValue(v); //update state to value from storage
                    ref.current = v; //update ref
                }
            } catch (e) {
                //fallback to reset storage value
                sessionStorage.setItem(key, JSON.stringify({ v: initialValue }));
                window.dispatchEvent(new Event("setSessionStorage"));
                setValue(initialValue); //initialize state
                ref.current = initialValue; //initialize ref
            }
        }

        //parse value on initial load
        LoadParseStorage();
        //update when ever storage gets changed
        window.addEventListener("setSessionStorage", LoadParseStorage);
        return () => {
            //remove listener
            window.removeEventListener("setSessionStorage", LoadParseStorage)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return [value, setValue_event];
}